import { GripVertical, Eye, EyeOff } from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { blockLabel, resolveBlockOrder } from "@/lib/blocks";

function SortableRow({ id, idx, hidden, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : hidden ? 0.55 : 1,
    zIndex: isDragging ? 50 : "auto",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`glass rounded-2xl p-4 ${isDragging ? "ring-2 ring-warmgold shadow-2xl" : ""} ${hidden ? "border-dashed" : ""}`}
      data-testid={`block-row-${id}`}
    >
      <div className="flex items-center gap-3">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing touch-none p-2 -ml-2 rounded hover:bg-warmgold/10 text-inksoft/60 hover:text-warmgold transition-colors"
          data-testid={`block-drag-${id}`}
          aria-label="Arrastrar para reordenar"
          title="Arrastra para reordenar"
        >
          <GripVertical size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${hidden ? "text-inksoft line-through" : "text-ink"}`}>
            <span className="text-inksoft/60 mr-1.5">{idx + 1}.</span>{blockLabel(id)}
          </p>
        </div>
        <label
          className={`shrink-0 inline-flex items-center gap-2 cursor-pointer select-none rounded-full px-3 py-1.5 border transition-colors ${hidden ? "border-inksoft/30 bg-inksoft/5 text-inksoft" : "border-warmgold/50 bg-warmgold/10 text-warmgold"}`}
          data-testid={`block-visibility-label-${id}`}
        >
          <input
            type="checkbox"
            checked={!hidden}
            onChange={(e) => onToggle(id, !e.target.checked)}
            className="hidden"
            data-testid={`block-visibility-${id}`}
          />
          {hidden ? <EyeOff size={14} /> : <Eye size={14} />}
          <span className="text-[11px] uppercase tracking-widest font-medium">{hidden ? "Oculta" : "Visible"}</span>
        </label>
      </div>
    </div>
  );
}

export default function BlockOrderEditor({ order, hiddenBlocks, onOrderChange, onHiddenChange }) {
  const list = resolveBlockOrder(order);
  const hiddenSet = new Set(hiddenBlocks || []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = list.indexOf(active.id);
    const newIndex = list.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onOrderChange(arrayMove(list, oldIndex, newIndex));
  };

  const handleToggle = (id, shouldHide) => {
    const next = new Set(hiddenSet);
    if (shouldHide) next.add(id);
    else next.delete(id);
    onHiddenChange(Array.from(next));
  };

  return (
    <div className="space-y-3" data-testid="block-order-editor">
      <p className="text-xs text-inksoft">La portada y la confirmación (RSVP) son fijas. Arrastra los puntitos <span className="inline-block align-middle">≡</span> para reordenar, y usa el botón Visible/Oculta para mostrar u ocultar cada sección.</p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={list} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {list.map((id, idx) => (
              <SortableRow key={id} id={id} idx={idx} hidden={hiddenSet.has(id)} onToggle={handleToggle} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
